import { Clock, ChevronRight } from 'lucide-react';
import { getSubjectStyle } from './homeworkTheme';
import type { StudentWorkItem } from './workItem';

interface DueTodayWidgetProps {
  dueTodayTasks: StudentWorkItem[];
  onViewAllDueToday: () => void;
}

/** Cards the side widget shows; the rest are a "View all" away in the main list. */
const DUE_TODAY_CARD_LIMIT = 3;

export function DueTodayWidget({ dueTodayTasks, onViewAllDueToday }: DueTodayWidgetProps) {
  return (
    <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-5 shadow-xs flex flex-col gap-3.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-500" />
          <h4 className="text-base font-bold text-slate-900 dark:text-white tracking-tight">Due Today</h4>
        </div>
        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#fff7ed] text-[#ea580c] dark:bg-orange-950/40 dark:text-orange-300 border border-[#fed7aa] dark:border-orange-800/60">
          {dueTodayTasks.length}
        </span>
      </div>

      {/* Mini List */}
      {dueTodayTasks.length > 0 ? (
        <div className="flex flex-col gap-2.5">
          {dueTodayTasks.slice(0, DUE_TODAY_CARD_LIMIT).map((hw) => {
            const theme = getSubjectStyle(hw.subject.name);
            return (
              <div
                key={hw.key}
                className="rounded-xl bg-slate-50 dark:bg-[#131528] border border-slate-200/60 dark:border-white/5 p-3 flex items-start gap-3"
              >
                <div className={`grid h-8 w-8 place-items-center rounded-lg shrink-0 text-xs font-bold ${theme.iconBg}`}>
                  {theme.icon}
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-1">
                    <span className={`text-[11px] uppercase ${theme.textAccent}`}>{hw.subject.name}</span>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-800/50">
                      Today
                    </span>
                  </div>
                  <h5 className="text-xs font-bold text-slate-900 dark:text-white truncate mt-0.5">{hw.title}</h5>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">{hw.assignedBy}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="py-4 text-center">
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">No tasks due today 🎉</p>
        </div>
      )}

      {/* Only when some are left out — a link to a list the widget already
          shows in full promises nothing new. */}
      {dueTodayTasks.length > DUE_TODAY_CARD_LIMIT && (
        <button
          type="button"
          onClick={onViewAllDueToday}
          className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center justify-center gap-1 mt-1 cursor-pointer"
        >
          <span>View all {dueTodayTasks.length} due today</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
